/**
 * Reads the files of a zip archive, stored or deflated, with the platform's `DecompressionStream`
 * (no library). Enough for a driver package (dashboard-editor spec C-7): no encryption, no ZIP64,
 * no archives split over files. The unpacked size is capped, against archives built to blow up.
 */

export interface ZipEntry {
    /** The path in the archive, with `/` between folders. */
    readonly name: string;
    readonly bytes: Uint8Array;
}

export class ZipError extends Error {}

const END_OF_DIRECTORY = 0x06054b50;
const DIRECTORY_ENTRY = 0x02014b50;
const LOCAL_HEADER = 0x04034b50;
const STORED = 0;
const DEFLATED = 8;

// The platform's streams and text decoder, declared as this package uses them (no DOM types here).
interface ByteStream {
    pipeThrough(transform: unknown) : ByteStream;
    getReader() : { read() : Promise<{ done: boolean; value?: Uint8Array }> };
}
declare const ReadableStream: new (source: { start(controller: { enqueue(chunk: Uint8Array) : void; close() : void }) : void }) => ByteStream;
declare const DecompressionStream: new (format: 'deflate-raw') => unknown;
declare const TextDecoder: new () => { decode(bytes: Uint8Array) : string };

async function inflateRaw(data: Uint8Array) : Promise<Uint8Array> {
    const stream = new ReadableStream({
        start(controller) {
            controller.enqueue(data);
            controller.close();
        },
    }).pipeThrough(new DecompressionStream('deflate-raw'));
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let length = 0;
    for (let chunk = await reader.read(); !chunk.done; chunk = await reader.read()) {
        if (chunk.value) {
            chunks.push(chunk.value);
            length += chunk.value.byteLength;
        }
    }

    const bytes = new Uint8Array(length);
    let offset = 0;
    for (const chunk of chunks) {
        bytes.set(chunk, offset);
        offset += chunk.byteLength;
    }

    return bytes;
}

/** The end-of-directory record: from the end, past a comment of up to 64 KiB. */
function findEndOfDirectory(view: DataView) : number {
    const earliest = Math.max(0, view.byteLength - 22 - 0xffff);
    for (let offset = view.byteLength - 22; offset >= earliest; offset--) {
        if (view.getUint32(offset, true) === END_OF_DIRECTORY) {
            return offset;
        }
    }

    throw new ZipError('not a zip file');
}

/**
 * The files in a zip archive, folders left out. `maxBytes` caps the unpacked size of all of them
 * together; an archive that would unpack to more is refused.
 */
export async function readZip(archive: Uint8Array, maxBytes: number) : Promise<ZipEntry[]> {
    const view = new DataView(archive.buffer, archive.byteOffset, archive.byteLength);
    if (archive.byteLength < 22) {
        throw new ZipError('not a zip file');
    }

    const end = findEndOfDirectory(view);
    const count = view.getUint16(end + 10, true);
    let offset = view.getUint32(end + 16, true);
    if (offset === 0xffffffff || count === 0xffff) {
        throw new ZipError('ZIP64 archives are not supported');
    }

    const decoder = new TextDecoder();
    const entries: ZipEntry[] = [];
    let total = 0;
    for (let index = 0; index < count; index++) {
        if (offset + 46 > archive.byteLength || view.getUint32(offset, true) !== DIRECTORY_ENTRY) {
            throw new ZipError('the zip file is damaged');
        }

        const flags = view.getUint16(offset + 8, true);
        const method = view.getUint16(offset + 10, true);
        const compressedSize = view.getUint32(offset + 20, true);
        const size = view.getUint32(offset + 24, true);
        const nameLength = view.getUint16(offset + 28, true);
        const extraLength = view.getUint16(offset + 30, true);
        const commentLength = view.getUint16(offset + 32, true);
        const localOffset = view.getUint32(offset + 42, true);
        const name = decoder.decode(archive.subarray(offset + 46, offset + 46 + nameLength));
        offset += 46 + nameLength + extraLength + commentLength;

        if (name.endsWith('/')) {
            continue;
        }
        if ((flags & 1) !== 0) {
            throw new ZipError(`${name} is encrypted`);
        }
        if (method !== STORED && method !== DEFLATED) {
            throw new ZipError(`${name} is compressed in a way that is not supported`);
        }

        total += size;
        if (total > maxBytes) {
            throw new ZipError(`the zip file unpacks to more than ${Math.round(maxBytes / 1024 / 1024)} MB`);
        }

        if (localOffset + 30 > archive.byteLength || view.getUint32(localOffset, true) !== LOCAL_HEADER) {
            throw new ZipError('the zip file is damaged');
        }

        const start = localOffset + 30 + view.getUint16(localOffset + 26, true) + view.getUint16(localOffset + 28, true);
        const data = archive.subarray(start, start + compressedSize);
        const bytes = method === STORED ? data.slice() : await inflateRaw(data);
        if (bytes.byteLength !== size) {
            throw new ZipError(`${name} is not the size the zip file says`);
        }
        entries.push({ name, bytes });
    }

    return entries;
}
